import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Save, Send, Apple, Smartphone, Palette, Image as ImageIcon, Eye } from "lucide-react";
import { toast } from "sonner";
import { usePersistedState } from "@/lib/persisted-state";
import QRCode from "qrcode";
import { useEventMeta } from "@/lib/event-meta";
import { API_BASE } from "@/lib/utils";
import WorkspaceMediaField from "@/components/WorkspaceMediaField";
import VenueLocationPicker from "@/components/VenueLocationPicker";
import { resolveMediaUrl } from "@/lib/media-api";
import { fetchBadgeDataSources } from "@/lib/badge-template";
import {
  WALLET_PASS_SLOTS,
  buildQrPayload,
  buildSourceOptions,
  defaultFieldMappings,
  normalizeWalletPassConfig,
  resolvePassFields,
} from "@/lib/wallet-pass-fields";
import {
  formatPassDate,
  formatPassTime,
  locationIsConfigured,
  normalizeVenueLocation,
  venueShortLabel,
} from "@/lib/wallet-format";

const presets = [
  { name: "Default — Acid", bg: "#09090B", fg: "#FAFAFA", label: "#71717A", accent: "var(--primary)" },
  { name: "Terracotta", bg: "#1A0E08", fg: "#FAFAFA", label: "#A1A1AA", accent: "#E25D3A" },
  { name: "Ivory", bg: "#FAFAFA", fg: "#09090B", label: "#71717A", accent: "#09090B" },
  { name: "Ice Blue", bg: "#0C1322", fg: "#FAFAFA", label: "#94A3B8", accent: "#60A5FA" },
];

export default function WalletPass() {
  const { id } = useParams();
  const e = useEventMeta(id);
  const eventId = e.id || id;
  const [savingNow, setSavingNow] = useState(false);
  const [dataSources, setDataSources] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [previewAttendees, setPreviewAttendees] = useState([]);
  const { value, setValue, statusLabel } = usePersistedState(`wallet-pass.${id}`, {
    preset: 0,
    orgName: "AivoryEMS",
    passType: "event_ticket",
    fieldMappings: defaultFieldMappings("Gate A"),
    previewAttendeeId: "",
    assets: { logo: "", strip: "", icon: "" },
    pushTriggers: {
      scheduleUpdate: true,
      t24Reminder: true,
      doorOpen: true,
      tierUpgrade: false,
    },
    doorsTime: "",
    venueLocation: null,
    distributionLog: [],
  });

  const config = useMemo(() => normalizeWalletPassConfig(value, e.title), [value, e.title]);
  const preset = config.preset ?? 0;
  const orgName = config.orgName ?? "AivoryEMS";
  const passType = config.passType ?? "event_ticket";
  const assets = config.assets || { logo: "", strip: "", icon: "" };
  const pushTriggers = config.pushTriggers || {};
  const fieldMappings = config.fieldMappings || defaultFieldMappings("Gate A");
  const previewAttendeeId = config.previewAttendeeId || "";
  const doorsTime = config.doorsTime ?? "";
  const venueLocation = useMemo(
    () => normalizeVenueLocation(config.venueLocation, e),
    [config.venueLocation, e.title, e.venue]
  );
  const distributionLog = Array.isArray(config.distributionLog) ? config.distributionLog : [];

  const setPreset = (next) => setValue((prev) => ({ ...prev, preset: next }));
  const setOrgName = (next) => setValue((prev) => ({ ...prev, orgName: next }));
  const setPassType = (next) => setValue((prev) => ({ ...prev, passType: next }));
  const setAsset = (key, val) => setValue((prev) => ({ ...prev, assets: { ...(prev.assets || {}), [key]: val } }));
  const setPushTrigger = (key) =>
    setValue((prev) => ({ ...prev, pushTriggers: { ...(prev.pushTriggers || {}), [key]: !prev.pushTriggers?.[key] } }));
  const setDoorsTime = (next) => setValue((prev) => ({ ...prev, doorsTime: next }));
  const setVenueLocation = (next) => setValue((prev) => ({ ...prev, venueLocation: next }));
  const setPreviewAttendeeId = (next) => setValue((prev) => ({ ...prev, previewAttendeeId: next }));

  const updateFieldMapping = (slotId, patch) => {
    setValue((prev) => {
      const normalized = normalizeWalletPassConfig(prev, e.title);
      return {
        ...prev,
        fieldMappings: {
          ...normalized.fieldMappings,
          [slotId]: { ...normalized.fieldMappings[slotId], ...patch },
        },
      };
    });
  };

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    (async () => {
      try {
        const [sources, attRes, formRes] = await Promise.all([
          fetchBadgeDataSources(eventId),
          fetch(`${API_BASE}/api/events/${eventId}/attendees?limit=50&include_answers=true`),
          fetch(`${API_BASE}/api/events/${eventId}/form-builder/published`),
        ]);
        if (cancelled) return;
        setDataSources(sources);
        if (attRes.ok) {
          const attPayload = await attRes.json();
          setPreviewAttendees(Array.isArray(attPayload.attendees) ? attPayload.attendees : []);
        }
        if (formRes.ok) {
          const formPayload = await formRes.json();
          const fields = formPayload?.value?.fields;
          setFormFields(Array.isArray(fields) ? fields : []);
        }
      } catch {
        if (!cancelled) {
          setDataSources(null);
          setPreviewAttendees([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const sourceOptions = useMemo(() => buildSourceOptions(dataSources, formFields), [dataSources, formFields]);

  const previewAttendee = useMemo(() => {
    if (previewAttendeeId) {
      const found = previewAttendees.find((a) => a.id === previewAttendeeId);
      if (found) return found;
    }
    if (previewAttendees[0]) return previewAttendees[0];
    return dataSources?.attendee?.sample || {};
  }, [previewAttendeeId, previewAttendees, dataSources]);

  const customAnswers = previewAttendee?.customAnswers || previewAttendee?.answers || {};
  const qrCode = buildQrPayload(eventId, previewAttendee?.id);

  const resolvedFields = useMemo(
    () =>
      resolvePassFields(fieldMappings, {
        event: e,
        attendee: previewAttendee,
        customAnswers,
        qrCode,
      }),
    [fieldMappings, e, previewAttendee, customAnswers, qrCode]
  );

  const brandPreset = {
    name: "Event Brand",
    bg: "#09090B",
    fg: "#FAFAFA",
    label: e.secondaryAccent || "#A1A1AA",
    accent: e.accent || "var(--primary)",
  };
  const palette = [brandPreset, ...presets];
  const t = palette[preset] || palette[0];

  const passDoors = doorsTime || formatPassTime(e.startDate, e.timezone);
  const passDate = formatPassDate(e.startDate, e.timezone);
  const venueLabel = venueShortLabel(e.venue) || venueLocation.label || e.title;

  const saveNow = async () => {
    if (pushTriggers.doorOpen && !locationIsConfigured(venueLocation)) {
      toast.error("Set a venue pin before enabling door-open reminders");
      return;
    }
    const payload = {
      ...config,
      fieldMappings,
      previewAttendeeId,
      venueLocation,
      doorsTime,
    };
    try {
      setSavingNow(true);
      const res = await fetch(`${API_BASE}/api/state/${encodeURIComponent(`wallet-pass.${id}`)}`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ value: payload }),
      });
      if (!res.ok) throw new Error("save failed");
      toast.success("Wallet pass template saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSavingNow(false);
    }
  };

  const previewOnDevice = () => {
    const primary = resolvedFields.primary?.value || "Attendee";
    const ticket = resolvedFields.secondary1?.value || "—";
    const ref = resolvedFields.auxiliary?.value || "—";
    const html = `<!doctype html><html><head><title>Wallet Preview</title><style>body{margin:0;background:var(--bg-base,#0A0A0B);color:var(--text-primary,#FAFAFA);font-family:sans-serif;display:grid;place-items:center;min-height:100vh}.card{width:360px;background:${t.bg};color:${t.fg};border:1px solid var(--border-default,#27272A);border-radius:16px;overflow:hidden}.strip{height:90px;background:url('${assets.strip || e.cover}') center/cover no-repeat}.pad{padding:16px}.muted{opacity:.7;font-size:11px;text-transform:uppercase;letter-spacing:.12em}.row{display:flex;justify-content:space-between;gap:12px;margin-top:12px}.qr{display:grid;place-items:center;background:#fff;padding:14px;margin:16px;border-radius:8px}</style></head><body><div class="card"><div class="strip"></div><div class="pad"><div class="muted">${orgName}</div><h2 style="margin:8px 0 0">${primary}</h2><div style="margin-top:6px">${e.title}</div><div class="row"><div><div class="muted">${resolvedFields.secondary1?.label || "Ticket"}</div><div>${ticket}</div></div><div><div class="muted">${resolvedFields.auxiliary?.label || "Ref"}</div><div>${ref}</div></div></div></div><div class="qr"><div id="qr">${qrCode || ref}</div></div></div></body></html>`;
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(html);
    win.document.close();
  };

  const distributePasses = () => {
    const recipientBase = Math.max(0, Number(e.registered || 0));
    const alreadyAdded = distributionLog.reduce((sum, row) => sum + Math.max(0, Number(row?.added || 0)), 0);
    const remaining = Math.max(0, recipientBase - alreadyAdded);
    const addedNow = remaining === 0 ? 0 : Math.min(remaining, Math.max(1, Math.ceil(remaining * 0.5)));
    const run = {
      id: `d_${Date.now()}`,
      ts: new Date().toISOString().replace("T", " ").slice(0, 16) + " UTC",
      event: "Manual distribution",
      cohort: "All confirmed",
      recipients: recipientBase,
      added: addedNow,
    };
    setValue((prev) => ({ ...prev, distributionLog: [run, ...(prev.distributionLog || [])] }));
    toast.success("Distribution queued", {
      description: `${run.recipients.toLocaleString()} recipients · each pass uses attendee field mappings`,
    });
  };

  const walletKpis = useMemo(() => {
    const recipients = distributionLog.reduce((sum, row) => sum + Math.max(0, Number(row?.recipients || 0)), 0);
    const added = distributionLog.reduce((sum, row) => sum + Math.max(0, Number(row?.added || 0)), 0);
    const runs = distributionLog.length;
    const adoptionPct = recipients > 0 ? Math.round((added / recipients) * 100) : 0;
    return { recipients, added, runs, adoptionPct };
  }, [distributionLog]);

  return (
    <div className="h-full min-h-0 flex flex-col" data-testid="wallet-page">
      <div className="sticky top-0 z-20 bg-[var(--bg-base-alpha95)] backdrop-blur border-b border-[var(--border-default)] p-4 md:p-6 lg:px-8">
        <div className="max-w-[1500px] mx-auto flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-overline">{e.title} · wallet passes</div>
            <h1 className="font-heading text-3xl mt-2 tracking-tight font-medium">Mobile wallet</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-2">
              Map pass slots to attendee list fields. Each distributed pass is filled per person at send time.
            </p>
          </div>
          <div className="flex gap-2">
            {statusLabel ? <div className="text-xs font-mono text-[var(--text-secondary)] self-center mr-1">{statusLabel}</div> : null}
            <button type="button" className="btn-secondary" onClick={previewOnDevice}>
              <Eye size={14} /> Preview on device
            </button>
            <button type="button" onClick={saveNow} disabled={savingNow} className="btn-secondary">
              <Save size={14} /> {savingNow ? "Saving..." : "Save"}
            </button>
            <button type="button" onClick={distributePasses} className="btn-primary" data-testid="distribute-passes">
              <Send size={14} /> Distribute to attendees
            </button>
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6 lg:p-8">
        <div className="max-w-[1500px] mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
            <KPI k="Distribution runs" v={walletKpis.runs.toLocaleString()} delta={`${walletKpis.recipients.toLocaleString()} recipients`} />
            <KPI k="Added to wallet" v={walletKpis.added.toLocaleString()} delta={`${walletKpis.adoptionPct}% adoption`} />
            <KPI k="Registered" v={Number(e.registered || 0).toLocaleString()} delta="Event attendees" />
            <KPI k="Last run" v={distributionLog[0]?.ts?.slice(0, 10) || "—"} delta={distributionLog[0]?.event || "No runs yet"} />
          </div>

          <div className="mt-8 md:h-[70vh] min-h-0 grid grid-cols-1 md:grid-cols-12 gap-px bg-[var(--border-default)] border border-[var(--border-default)] overflow-hidden">
            <div className="col-span-1 md:col-span-4 min-h-0 overflow-y-auto bg-[var(--bg-surface)] p-4 md:p-6 space-y-6">
              <div>
                <div className="flex items-center gap-2 text-overline mb-3">
                  <Palette size={11} /> Color preset
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {palette.map((p, i) => (
                    <button
                      key={p.name}
                      type="button"
                      onClick={() => setPreset(i)}
                      data-testid={`preset-${i}`}
                      className={`p-3 border text-left transition ${preset === i ? "border-[var(--primary)]" : "border-[var(--border-default)] hover:border-[var(--border-hover)]"}`}
                      style={{ background: p.bg }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-3 h-3" style={{ background: p.accent }} />
                        <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: p.fg }}>
                          {p.name.split(" — ")[0]}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <Field label="Organization">
                <input value={orgName} onChange={(ev) => setOrgName(ev.target.value)} className="input-dark" />
              </Field>
              <Field label="Pass type">
                <select className="input-dark" value={passType} onChange={(ev) => setPassType(ev.target.value)}>
                  <option value="event_ticket">Event ticket</option>
                  <option value="boarding_pass">Boarding pass</option>
                  <option value="generic">Generic</option>
                </select>
              </Field>

              <div className="surface p-4">
                <div className="text-overline mb-1">Pass field mapping</div>
                <p className="text-xs text-[var(--text-secondary)] mb-4 leading-relaxed">
                  Each wallet slot pulls from the attendee list (or event). Preview uses the attendee selected on the right.
                </p>
                <div className="space-y-3">
                  {WALLET_PASS_SLOTS.filter((slot) => slot.id !== "qr").map((slot) => {
                    const mapping = fieldMappings[slot.id] || {};
                    const resolved = resolvedFields[slot.id];
                    const isFixed = Boolean(slot.fixedSource);
                    const source = slot.fixedSource || mapping.source || slot.defaultSource;
                    return (
                      <div key={slot.id} className="border border-[var(--border-default)] p-3 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)]">{slot.role}</span>
                          {!isFixed ? (
                            <input
                              value={mapping.label || slot.defaultLabel}
                              onChange={(ev) => updateFieldMapping(slot.id, { label: ev.target.value })}
                              className="input-dark text-[10px] font-mono uppercase max-w-[120px] py-1"
                              placeholder="Label"
                            />
                          ) : (
                            <span className="text-[10px] font-mono uppercase text-[var(--text-secondary)]">{slot.defaultLabel}</span>
                          )}
                        </div>
                        {isFixed ? (
                          <div className="text-xs text-[var(--text-secondary)]">Event · title (automatic)</div>
                        ) : (
                          <select
                            className="input-dark text-xs w-full"
                            value={source}
                            onChange={(ev) => updateFieldMapping(slot.id, { source: ev.target.value })}
                          >
                            {sourceOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        )}
                        {source === "static" && !isFixed ? (
                          <input
                            value={mapping.staticValue ?? ""}
                            onChange={(ev) => updateFieldMapping(slot.id, { staticValue: ev.target.value })}
                            placeholder="Same value on every pass"
                            className="input-dark text-xs w-full font-mono"
                          />
                        ) : null}
                        <div className="text-[10px] font-mono text-[var(--text-tertiary)] truncate" title={resolved?.value}>
                          Preview: <span className="text-[var(--text-secondary)]">{resolved?.value || "—"}</span>
                        </div>
                      </div>
                    );
                  })}
                  <div className="border border-[var(--border-default)] p-3">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-1">Barcode</div>
                    <div className="text-xs text-[var(--text-secondary)]">Badge QR (B1|event|attendee|id)</div>
                    <div className="text-[10px] font-mono text-[var(--text-tertiary)] mt-2 truncate">{qrCode || "Select an attendee to preview QR"}</div>
                  </div>
                </div>
              </div>

              <div className="surface p-4">
                <Field label="Doors open (override)">
                  <input
                    value={doorsTime}
                    onChange={(ev) => setDoorsTime(ev.target.value)}
                    placeholder={formatPassTime(e.startDate, e.timezone) || "Uses event start time"}
                    className="input-dark font-mono text-xs"
                  />
                </Field>
              </div>

              <div className="surface p-4">
                <VenueLocationPicker eventId={id} eventVenue={e.venue} location={venueLocation} onChange={setVenueLocation} />
              </div>

              <div className="surface p-4 space-y-4">
                <div className="text-overline flex items-center gap-2">
                  <ImageIcon size={11} /> Visual assets
                </div>
                <WorkspaceMediaField label="Logo (PNG · 160×50)" value={assets.logo} onChange={(v) => setAsset("logo", v)} />
                <WorkspaceMediaField label="Strip image (1125×432)" value={assets.strip} onChange={(v) => setAsset("strip", v)} />
                <WorkspaceMediaField label="Icon (29×29)" value={assets.icon} onChange={(v) => setAsset("icon", v)} />
              </div>

              <div className="surface p-4">
                <div className="text-overline mb-3">Push triggers</div>
                <div className="space-y-2 text-xs text-[var(--text-secondary)]">
                  {[
                    ["Update pass on schedule change", "scheduleUpdate"],
                    ["Send T-24h reminder via Wallet", "t24Reminder"],
                    ["Show 'Door is now open' lock-screen", "doorOpen"],
                    ["Update tier after upgrade", "tierUpgrade"],
                  ].map(([n, key]) => (
                    <div key={n} className="flex items-center justify-between py-1.5">
                      <span>{n}</span>
                      <button
                        type="button"
                        onClick={() => setPushTrigger(key)}
                        className={`w-9 h-5 relative ${pushTriggers[key] ? "bg-[var(--primary)]" : "bg-[var(--border-default)]"}`}
                      >
                        <span
                          className={`absolute top-0.5 w-4 h-4 bg-[var(--bg-base)] transition-all ${pushTriggers[key] ? "left-4" : "left-0.5"}`}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="col-span-1 md:col-span-8 min-h-0 overflow-y-auto bg-[var(--bg-elev)] dot-grid p-4 md:p-6 lg:p-8">
              <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
                <div>
                  <div className="text-overline">Live preview</div>
                  <p className="text-xs text-[var(--text-secondary)] mt-1">Resolved from attendee list using your field mappings above.</p>
                </div>
                <label className="block min-w-[240px]">
                  <span className="text-overline">Preview attendee</span>
                  <select
                    className="input-dark mt-2 w-full text-xs"
                    value={previewAttendee?.id || ""}
                    onChange={(ev) => setPreviewAttendeeId(ev.target.value)}
                  >
                    {previewAttendees.length === 0 ? <option value="">No attendees yet</option> : null}
                    {previewAttendees.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name || a.email || a.id} · {a.ticket || a.status || "—"}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
                <div>
                  <div className="flex items-center gap-2 text-overline mb-4">
                    <Apple size={12} /> APPLE WALLET
                  </div>
                  <ApplePass
                    t={t}
                    event={e}
                    org={orgName}
                    assets={assets}
                    fields={resolvedFields}
                    passDate={passDate}
                    passDoors={passDoors}
                    venueLabel={venueLabel}
                    qrCode={qrCode}
                    geofenceReady={locationIsConfigured(venueLocation)}
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2 text-overline mb-4">
                    <Smartphone size={12} /> GOOGLE WALLET
                  </div>
                  <GooglePass
                    t={t}
                    event={e}
                    org={orgName}
                    assets={assets}
                    fields={resolvedFields}
                    passDoors={passDoors}
                    qrCode={qrCode}
                    geofenceReady={locationIsConfigured(venueLocation)}
                  />
                </div>
              </div>
            </div>
          </div>

          <h2 className="font-heading text-2xl mt-14 mb-4">Distribution activity</h2>
          <div className="surface overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="border-b border-[var(--border-default)] text-[var(--text-tertiary)] font-mono text-[10px] uppercase tracking-widest">
                <tr>
                  <th className="p-4 text-left">Time</th>
                  <th className="p-4 text-left">Event</th>
                  <th className="p-4 text-left">Cohort</th>
                  <th className="p-4 text-right">Recipients</th>
                  <th className="p-4 text-right">Added to wallet</th>
                </tr>
              </thead>
              <tbody>
                {distributionLog.map((r) => (
                  <tr key={r.id} className="border-b border-[var(--border-default)]">
                    <td className="p-4 font-mono text-xs text-[var(--text-secondary)] whitespace-nowrap">{r.ts}</td>
                    <td className="p-4">{r.event}</td>
                    <td className="p-4 text-[var(--text-secondary)]">{r.cohort}</td>
                    <td className="p-4 text-right font-mono whitespace-nowrap">
                      {typeof r.recipients === "number" ? r.recipients.toLocaleString() : r.recipients}
                    </td>
                    <td className="p-4 text-right font-mono text-[var(--primary)] whitespace-nowrap">
                      {typeof r.added === "number" ? r.added.toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function ApplePass({ t, event, org, assets, fields, passDate, passDoors, venueLabel, qrCode, geofenceReady }) {
  return (
    <div className="mx-auto max-w-[320px] shadow-[0_30px_60px_rgba(0,0,0,0.6)] overflow-hidden" style={{ background: t.bg, color: t.fg, borderRadius: 18 }}>
      <div className="px-5 pt-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center overflow-hidden" style={{ background: t.accent, color: t.bg, borderRadius: 6 }}>
            {assets.logo ? (
              <img src={resolveMediaUrl(assets.logo)} alt="" className="w-full h-full object-cover" />
            ) : (
              <svg width="16" height="16" viewBox="0 0 32 32" fill="none">
                <path d="M9 22 L16 8 L23 22 Z" fill={t.bg} />
              </svg>
            )}
          </div>
          <div className="text-[10px] font-medium" style={{ color: t.label }}>
            {org}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[9px] uppercase tracking-widest" style={{ color: t.label }}>
            {fields.header?.label || "EVENT"}
          </div>
          <div className="text-[11px] font-medium">{fields.header?.value || event.title}</div>
        </div>
      </div>

      <div className="mt-4 mx-5 h-20 relative overflow-hidden" style={{ borderRadius: 8 }}>
        <img src={resolveMediaUrl(assets.strip || event.cover)} className="w-full h-full object-cover" alt="" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${t.bg}E0, transparent)` }} />
        <div className="absolute bottom-2 left-3 text-xs font-medium max-w-[85%] truncate" style={{ color: t.fg }} title={venueLabel}>
          {venueLabel || "Venue TBD"}
        </div>
      </div>

      {geofenceReady ? (
        <div className="mx-5 mt-2 px-2 py-1 text-[9px] font-mono uppercase tracking-widest border border-[var(--primary)]/30 text-[var(--primary)]">
          Proximity reminder enabled
        </div>
      ) : null}

      <div className="px-5 pt-5">
        <div className="text-[9px] uppercase tracking-widest" style={{ color: t.label }}>
          {fields.primary?.label || "ATTENDEE"}
        </div>
        <div className="text-2xl font-medium tracking-tight mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
          {fields.primary?.value || "—"}
        </div>
      </div>

      <div className="px-5 pt-4 grid grid-cols-2 gap-4">
        <div>
          <div className="text-[9px] uppercase tracking-widest" style={{ color: t.label }}>
            {fields.secondary1?.label || "TICKET"}
          </div>
          <div className="text-sm font-medium mt-1" style={{ color: t.accent }}>
            {fields.secondary1?.value || "—"}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest" style={{ color: t.label }}>
            {fields.secondary2?.label || "GATE"}
          </div>
          <div className="text-sm font-medium mt-1">{fields.secondary2?.value || "—"}</div>
        </div>
      </div>
      <div className="px-5 pt-3 grid grid-cols-2 gap-4">
        <div>
          <div className="text-[9px] uppercase tracking-widest" style={{ color: t.label }}>
            DATE
          </div>
          <div className="text-sm font-medium mt-1">{passDate}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest" style={{ color: t.label }}>
            DOORS
          </div>
          <div className="text-sm font-medium mt-1">{passDoors || "—"}</div>
        </div>
      </div>
      <div className="px-5 pt-3">
        <div className="text-[9px] uppercase tracking-widest" style={{ color: t.label }}>
          {fields.auxiliary?.label || "REF"}
        </div>
        <div className="text-sm font-medium mt-1 font-mono">{fields.auxiliary?.value || "—"}</div>
      </div>

      <div className="mt-5 mx-5 mb-5 p-4 flex items-center justify-center" style={{ background: "#FAFAFA" }}>
        <RealQr value={qrCode || fields.auxiliary?.value} />
      </div>
      <div className="px-5 pb-4 text-center text-[10px] font-mono truncate" style={{ color: t.label }}>
        {qrCode || fields.auxiliary?.value || "—"}
      </div>
    </div>
  );
}

function GooglePass({ t, event, org, assets, fields, passDoors, qrCode, geofenceReady }) {
  const dateLine = [event.startDate, event.endDate].filter(Boolean).join(" → ") || "Dates TBD";
  const venueLine = event.venue ? event.venue.split("\n")[0] : "";
  return (
    <div className="mx-auto max-w-[320px] shadow-[0_30px_60px_rgba(0,0,0,0.6)] overflow-hidden" style={{ background: t.bg, color: t.fg, borderRadius: 14 }}>
      <div className="px-5 py-4 flex items-center justify-between border-b" style={{ borderColor: `${t.label}33` }}>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 overflow-hidden" style={{ background: t.accent, borderRadius: 4 }}>
            {assets.icon ? <img src={resolveMediaUrl(assets.icon)} alt="" className="w-full h-full object-cover" /> : null}
          </div>
          <span className="text-xs font-medium">{org}</span>
        </div>
        <span className="text-[10px] uppercase tracking-widest" style={{ color: t.label }}>
          TICKET
        </span>
      </div>

      <div className="px-5 py-5" style={{ background: `linear-gradient(135deg, ${t.bg}, ${t.bg}DD)` }}>
        <div className="text-[10px] uppercase tracking-widest" style={{ color: t.label }}>
          {fields.header?.value || event.title}
        </div>
        <div className="text-2xl font-medium tracking-tight mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
          {fields.primary?.value || "—"}
        </div>
        <div className="text-xs mt-1" style={{ color: t.label }}>
          {dateLine}
          {venueLine ? ` · ${venueLine}` : ""}
        </div>
      </div>

      <div
        className="mx-5 -mt-3 px-3 py-1.5 inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest"
        style={{ background: t.accent, color: t.bg, borderRadius: 4 }}
      >
        <span>{fields.secondary1?.value || "—"}</span>
        <span style={{ opacity: 0.6 }}>·</span>
        <span>{fields.secondary2?.value || "—"}</span>
      </div>

      {geofenceReady ? (
        <div className="mx-5 mt-3 text-[9px] font-mono uppercase tracking-widest text-[var(--primary)]">Near-venue reminder on</div>
      ) : null}

      <div className="mt-5 mx-5 p-5 flex items-center justify-center" style={{ background: "#FAFAFA", borderRadius: 8 }}>
        <RealQr value={qrCode || fields.auxiliary?.value} size={120} />
      </div>
      <div className="px-5 mt-2 text-center text-[10px] font-mono truncate" style={{ color: t.label }}>
        {qrCode || fields.auxiliary?.value || "—"}
      </div>

      <div className="px-5 py-4 mt-2 grid grid-cols-2 gap-3 border-t" style={{ borderColor: `${t.label}33` }}>
        <div>
          <div className="text-[9px] uppercase tracking-widest" style={{ color: t.label }}>
            DOORS
          </div>
          <div className="text-xs font-medium mt-0.5">{passDoors || "—"}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-widest" style={{ color: t.label }}>
            {fields.auxiliary?.label || "REF"}
          </div>
          <div className="text-xs font-medium mt-0.5 font-mono">{fields.auxiliary?.value || "—"}</div>
        </div>
      </div>
    </div>
  );
}

function RealQr({ value, size = 100 }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(value || "preview", { width: size * 4, margin: 0, color: { dark: "#09090B", light: "#FFFFFF" } })
      .then((next) => {
        if (alive) setSrc(next);
      })
      .catch(() => {
        if (alive) setSrc("");
      });
    return () => {
      alive = false;
    };
  }, [value, size]);
  if (!src) return <div style={{ width: size, height: size }} className="bg-white" />;
  return <img src={src} alt="QR code" style={{ width: size, height: size }} className="object-contain" />;
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-overline">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function KPI({ k, v, delta }) {
  return (
    <div className="bg-[var(--bg-surface)] p-5">
      <div className="text-overline">{k}</div>
      <div className="font-mono text-2xl mt-2">{v}</div>
      <div className="text-xs font-mono text-[var(--primary)] mt-2">{delta}</div>
    </div>
  );
}
